import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Puzzle, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlayTherapySessionRecord {
  id: number;
  person_id: number;
  agency_id: number;
  session_type: string;
  session_date: string;
  session_details: string | null;
  toys_used: string | null;
  play_themes: string | null;
  staff_id: number | null;
  staff_first_name?: string;
  staff_last_name?: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface PlayTherapySessionsProps {
  personId: string;
}

const sessionTypeLabels: Record<string, string> = {
  individual: "Individual",
  group: "Group",
  family: "Family",
  sibling: "Sibling",
};

export function PlayTherapySessions({ personId }: PlayTherapySessionsProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingSession, setEditingSession] = useState<PlayTherapySessionRecord | null>(null);

  const { data, isLoading } = useQuery<{ data: PlayTherapySessionRecord[] }>({
    queryKey: ["playTherapySessions", personId],
    queryFn: () =>
      api.get<{ data: PlayTherapySessionRecord[] }>(
        `/clients/${personId}/play-therapy-sessions`,
      ),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post(`/clients/${personId}/play-therapy-sessions`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playTherapySessions", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      api.put(`/clients/${personId}/play-therapy-sessions/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playTherapySessions", personId] });
      setEditingSession(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) =>
      api.delete(`/clients/${personId}/play-therapy-sessions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["playTherapySessions", personId] });
    },
  });

  const sessions = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Play Therapy Sessions</h3>
        <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
          <Plus className="h-4 w-4" />
          Add Session
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <PlayTherapyForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingSession && (
        <Card>
          <CardContent className="p-4">
            <PlayTherapyForm
              initial={editingSession}
              onSubmit={(d) =>
                updateMutation.mutate({ id: editingSession.id, ...d })
              }
              onCancel={() => setEditingSession(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div
              key={i}
              className="h-20 animate-pulse rounded-lg border bg-muted"
            />
          ))}
        </div>
      )}

      {!isLoading && sessions.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Puzzle className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No play therapy sessions found.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading &&
        sessions.map((s) => {
          const staffName =
            s.staff_first_name && s.staff_last_name
              ? `${s.staff_first_name} ${s.staff_last_name}`
              : null;

          return (
            <Card key={s.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">
                      {sessionTypeLabels[s.session_type] ?? s.session_type} Session -{" "}
                      {new Date(s.session_date).toLocaleDateString()}
                    </CardTitle>
                    <CardDescription className="mt-0.5">
                      {staffName && `Therapist: ${staffName}`}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditingSession(s)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (confirm("Delete this session?")) {
                          deleteMutation.mutate(s.id);
                        }
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {s.toys_used && (
                  <p className="text-xs text-muted-foreground">
                    <span className="font-medium">Toys Used:</span> {s.toys_used}
                  </p>
                )}
                {s.play_themes && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-medium">Play Themes:</span> {s.play_themes}
                  </p>
                )}
                {s.session_details && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-medium">Details:</span> {s.session_details}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

function PlayTherapyForm({
  initial,
  onSubmit,
  onCancel,
  isLoading,
}: {
  initial?: PlayTherapySessionRecord;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [sessionType, setSessionType] = useState(initial?.session_type ?? "individual");
  const [sessionDate, setSessionDate] = useState(initial?.session_date ?? "");
  const [sessionDetails, setSessionDetails] = useState(initial?.session_details ?? "");
  const [toysUsed, setToysUsed] = useState(initial?.toys_used ?? "");
  const [playThemes, setPlayThemes] = useState(initial?.play_themes ?? "");

  function handleSubmit() {
    onSubmit({
      sessionType,
      sessionDate: sessionDate || undefined,
      sessionDetails: sessionDetails || undefined,
      toysUsed: toysUsed || undefined,
      playThemes: playThemes || undefined,
    });
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Session Type
          </label>
          <select
            value={sessionType}
            onChange={(e) => setSessionType(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="individual">Individual</option>
            <option value="group">Group</option>
            <option value="family">Family</option>
            <option value="sibling">Sibling</option>
          </select>
        </div>
        <Input
          label="Session Date"
          type="date"
          value={sessionDate}
          onChange={(e) => setSessionDate(e.target.value)}
          required
        />
      </div>

      <Input
        label="Toys Used"
        value={toysUsed}
        onChange={(e) => setToysUsed(e.target.value)}
        placeholder="e.g., sand tray, dolls, puppets..."
      />

      <Input
        label="Play Themes"
        value={playThemes}
        onChange={(e) => setPlayThemes(e.target.value)}
        placeholder="e.g., nurturing, aggression, control..."
      />

      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">
          Session Details
        </label>
        <textarea
          value={sessionDetails}
          onChange={(e) => setSessionDetails(e.target.value)}
          rows={3}
          placeholder="Session observations and notes..."
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="flex gap-3">
        <Button size="sm" onClick={handleSubmit} loading={isLoading}>
          {initial ? "Update Session" : "Create Session"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
